import { FC, useContext, useEffect, useRef } from "react";
import {
  County,
} from "@app/geography";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import RegionSet, { RegionGranularity } from "@app/region-sets/regionSet";
import LookupRegionSet, { keyFor } from "@app/region-sets/editor/lookupRegionSet";
import { ServiceAreaEditorContext } from "@app/region-sets/editor/ServiceAreaEditorContext";
import { ServiceAreaEditorCounties } from "@app/region-sets/editor/ServiceAreaEditorCounties";
import { ServiceAreaEditorStates } from "@app/region-sets/editor/ServiceAreaEditorStates";
import { countyKey, zipKey } from "@app/region-sets/editor/types";
import { CountyStatus } from "@app/projects/create/countyStatus";

interface ServiceAreaEditorProps {
  // county statuses are retrieved separately from county info, and are passed in here
  countyStatuses: CountyStatus[];
  // the existing region set from the edit project workflow
  regionSet?: RegionSet;
  // used for the add nearby counties functionality
  // we use CountyLite internally here, but that's just a subset of County, so this works
  updateCounties?: County[];

  // onComplete will be called on submit through the process outlined above
  onComplete: (regionSet: RegionSet) => void;
  // onUpdate passes a temporary result to the parent so it can determine whether to disable the submit button
  onUpdate: (lookupRegionSet: LookupRegionSet) => void;

  // the parent component tracks county status updates, so whenever a status changes we have to tell it
  // the parent has to keep track for the add nearby counties button
  handleCountyStatusUpdates: (countyStatuses: CountyStatus[]) => void;
}

export const ServiceAreaEditor: FC<ServiceAreaEditorProps> = (props: ServiceAreaEditorProps) => {
  const statePaneRef = useRef<HTMLDivElement>(null);
  const countyPaneRef = useRef<HTMLDivElement>(null);
  const editorContext = useContext(ServiceAreaEditorContext);

  // on mount only, let the parent know what's selected so it can disable the next/submit button
  useEffect(() => {
    const derived = deriveLookupFromRegionSet(props.regionSet);
    props.onUpdate(derived);
  }, []);

  // on initial load/after uploading a file, focus the first element in the state pane
  useEffect(() => {
    if (!editorContext.loading) {
      focusFirstInteractiveElement(statePaneRef);
    }
  }, [editorContext.loading]);

  // update the county statuses in the parent
  // this has to be kept up to date for the add nearby counties button
  useEffect(() => {
    if (!editorContext.countyLookup)
      return;

    // handleCountyStatusUpdates expects the status of every county, even if it's unselected
    // so our best bet is just mapping the entire countyLookup
    // it considers Unknown to be unselected, and will remove that county from its own state tracking
    // and it _has_ to be Unknown. undefined won't remove it, and the nearby counties calculation will be wrong
    const countyStatuses = Object.values(editorContext.countyLookup).map((county) => {
      let countyZips: string[] = [];
      const allCountyZips = editorContext.countyToZipLookup[county[countyKey]] || [];
      const selectedCountyZips = allCountyZips.filter((countyZip) => editorContext.zipLookup[zipKey(county.countySSA, countyZip)].checkboxState === CheckboxStateEnum.Checked);
      if (selectedCountyZips.length !== allCountyZips.length) {
        countyZips = selectedCountyZips;
      }
      return {
        countySSA: county[countyKey],
        countyZips,
        status: county.countyStatus,
      }
    });
    props.handleCountyStatusUpdates(countyStatuses);
  }, [editorContext.countyLookup]);

  // in case the parent sends counties to update, i.e. from add nearby counties
  useEffect(() => {
    if (props.updateCounties.length > 0) {
      const newSSAs = props.updateCounties.map((county) => county[countyKey]);
      const updatedStateLookup = structuredClone(editorContext.stateLookup);
      const updatedCountyLookup = structuredClone(editorContext.countyLookup);
      const updatedZipLookup = structuredClone(editorContext.zipLookup);
      newSSAs.forEach((newCountySSA) => {
        // we can't just call handleCheckboxChange for each new county because the updatedCountyLookups will overwrite each other
        const status = props.countyStatuses.find((countyStatusObj) => countyStatusObj[countyKey] === newCountySSA)?.status || RegionSetStatusType.Unknown;
        updatedCountyLookup[newCountySSA].countyStatus = status;

        (editorContext.countyToZipLookup[newCountySSA] || []).forEach((zipCode) => {
          updatedZipLookup[zipKey(newCountySSA, zipCode)].checkboxState = CheckboxStateEnum.Checked;
        })

        const stateAbbreviation = editorContext.countyLookup[newCountySSA].state;
        const stateCounties = editorContext.stateToCountyLookup[stateAbbreviation];
        const allCountiesIncluded = stateCounties.every((countySSA) => updatedCountyLookup[countySSA].countyStatus !== RegionSetStatusType.Unknown);
        const noCountiesIncluded = stateCounties.every((countySSA) => updatedCountyLookup[countySSA].countyStatus === RegionSetStatusType.Unknown);
        updatedStateLookup[stateAbbreviation].checkboxState = allCountiesIncluded ?
          CheckboxStateEnum.Checked :
          noCountiesIncluded ?
            CheckboxStateEnum.Unchecked :
            CheckboxStateEnum.Indeterminate;
      });

      editorContext.setStateLookup(updatedStateLookup);
      editorContext.setCountyLookup(updatedCountyLookup);
      editorContext.setZipLookup(updatedZipLookup);
    }
  }, [props.updateCounties]);

  // clear the county filter if the selected state changes or the filter is hidden
  useEffect(() => {
    editorContext.setCountyFilter("");
  }, [editorContext.selectedState]);

  useEffect(() => {
    if (!countyPaneRef.current)
      return;
    countyPaneRef.current.scrollTop = 0;
  }, [editorContext.selectedState]);

  // convert our lookups to a region set for the parent component to determine whether it should
  // disable the submit button
  useEffect(() => {
    if (!!props.onUpdate) {
      const newRegionSet = editorContext.convertLookupsToRegionSet();
      const derived = deriveLookupFromRegionSet(newRegionSet);
      props.onUpdate(derived);
    }
  }, [editorContext.stateLookup, editorContext.countyLookup, editorContext.zipLookup]);

  return (
    <div className="service-area-editor">
      <div className={`service-area-editor__loading-overlay${editorContext.loading ? " loading" : ""}`}>
        <LoadingSpinner />
      </div>
      <div className="service-area-editor__column service-area-editor__column--25">
        <div className="service-area-editor__file-uploader">
          <DragAndDropFileUploader
            apiKeyName="serviceAreaFile"
            title="Upload Service Area (*.qsa, *.csv)"
            filetype=".qsa,.csv"
            onUpload={editorContext.fileSubmit}
            small={true}
            testId="service-area-file-upload"
          />
        </div>
        {/* state pane */}
        <div>
          <ServiceAreaEditorStates statePaneRef={statePaneRef} />
        </div>
      </div>
      <div className="service-area-editor__column service-area-editor__column--75">
        {/* county pane */}
        <ServiceAreaEditorCounties countyPaneRef={countyPaneRef} />
      </div>
    </div>
  );
}

// These are legacy functions from the old editor, and they deal with LookupRegionSet objects
// LRS objects have no relation to the county/state/zip lookups in this file
const deriveLookupFromRegionSet = (regionSet?: RegionSet): LookupRegionSet => {
  const parent = new LookupRegionSet(null, RegionGranularity.MultiRegion);

  // If we haven't been provided a region set, just return the parent lookup as is.
  if (!regionSet) {
    return parent;
  }

  // If the derived item is already a MultiRegion, just use that.
  const derived = deriveLookupRecurse(regionSet);
  if (derived.granularity === RegionGranularity.MultiRegion) {
    return derived;
  }

  // Otherwise, embed the derived inside the parent and return.
  parent.children = {
    ...parent.children,
    [derived.key]: derived,
  };

  return parent;
}

const deriveLookupRecurse = (regionSet: RegionSet): LookupRegionSet => {
  const children = regionSet.partialRegionSets &&
    regionSet.partialRegionSets.reduce<{ [key: string]: LookupRegionSet }>((res, partial) => {
      res[keyFor(partial)] = deriveLookupRecurse(partial);
      return res;
    }, {});

  return new LookupRegionSet(regionSet.regionCode, regionSet.granularity, children, regionSet.subRegionCode);
}
